import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import ChoiceChips from "@/components/ChoiceChips";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import StatsContainer from "@/components/StatsContainer";
import {PlayerAPI} from "@/api/player";

import {dateFormat, rpeOptions, ERROR_OPERATION} from "@/utils/constants";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const VIEW_OPTIONS = [
	{value: "list", label: "Elenco"},
	{value: "stats", label: "Per settimana"},
];

const FIELD_DATE_FORMAT = "YYYY-MM-DD";

const STAFF_PERIODS = [
	{label: "7 giorni", days: 7},
	{label: "14 giorni", days: 14},
	{label: "30 giorni", days: 30},
];

const PLAYER_PERIODS = [
	{label: "7 giorni", days: 7},
	{label: "30 giorni", days: 30},
	{label: "3 mesi", days: 90},
];

function RPEPage(props) {
	const {handleNotification} = props;

	const userRole = useSelector((state) => state?.user?.roleName);
	const playerId = useSelector((state) => state?.user?.playerId);
	const isPlayer = userRole === "Player";

	const periods = isPlayer ? PLAYER_PERIODS : STAFF_PERIODS;
	const defaultDays = isPlayer ? 30 : 7;

	const [activePeriod, setActivePeriod] = useState(defaultDays);
	const [currentSwitchView, setCurrentSwitchView] = useState("list");
	const [loading, setLoading] = useState(false);
	const [rpe, setRpe] = useState([]);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		onlyPro: true,
	});

	const filtersData = {
		fromDate: moment()
			.subtract(activePeriod - 1, "days")
			.format(FIELD_DATE_FORMAT),
		toDate: moment().format(FIELD_DATE_FORMAT),
	};

	const getRPE = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.listRPE({
				...filtersData,
				playerId: isPlayer ? playerId : undefined,
			});
			setRpe(response || []);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (currentSwitchView === "list") {
			getRPE();
		}
	}, [activePeriod, currentSwitchView]);

	const filters = [
		{
			key: "fromDate",
			type: "date",
			label: "Da",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: filtersData.fromDate,
			setValue: () => {},
		},
		{
			key: "toDate",
			type: "date",
			label: "A",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: filtersData.toDate,
			setValue: () => {},
		},
	];

	const columns = [
		{
			label: "Data",
			key: "date",
			customRender: (item) => moment(item.rpeDate).format(dateFormat),
			small: true,
		},
	];

	if (!isPlayer) {
		columns.push({
			label: "Giocatore",
			key: "playerId",
			customRender: (item) =>
				item.player
					? item.player.firstName + " " + item.player.lastName
					: "---",
			align: "start",
		});
	}

	columns.push({
		label: "RPE",
		key: "rpe",
		customRender: (item) =>
			rpeOptions.find((o) => o.value === item.rpe)?.label ?? "---",
		align: "start",
	});

	const listConfiguration = {data: rpe};

	return (
		<Page className="rpe-container">
			<Loading visible={loading} />

			<Breadcrumbs
				current="RPE"
				listElements={
					isPlayer
						? undefined
						: VIEW_OPTIONS.map((o) => ({
								...o,
								onClick: () => setCurrentSwitchView(o.value),
							}))
				}
				currentListElement={currentSwitchView}
			/>

			{currentSwitchView === "list" && (
				<ChoiceChips
					options={periods.map((p) => ({value: p.days, label: p.label}))}
					value={activePeriod}
					onChange={setActivePeriod}
				/>
			)}

			{currentSwitchView === "list" ? (
				<CardsContainer
					tableComponent={
						<CardsTable
							filtersData={filters}
							configurationColumns={columns}
							data={rpe}
							defaultSortedField="rpeDate"
							defaultSortedOrder="descend"
						/>
					}
					listComponent={
						<CardsList
							type="rpe"
							breadcrumbs
							filtersData={filters}
							listConfiguration={listConfiguration}
							defaultSortedField="rpeDate"
							defaultSortedOrder="descend"
						/>
					}
				/>
			) : (
				<StatsContainer type="rpe" categories={categories} />
			)}
		</Page>
	);
}

export default RPEPage;
